
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle, CheckCircle, Info, Loader2 } from 'lucide-react';
import { testSupabaseConnection, ConnectionTestResult } from '@/lib/supabaseConnectionTest';

export function SupabaseConnectionTest() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ConnectionTestResult | null>(null);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [envDebug, setEnvDebug] = useState<any>({});
  
  useEffect(() => {
    // Collect environment debug info
    setEnvDebug({
      hasUrlMeta: Boolean(import.meta.env.VITE_SUPABASE_URL),
      hasKeyMeta: Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY),
      urlType: typeof import.meta.env.VITE_SUPABASE_URL,
      keyType: typeof import.meta.env.VITE_SUPABASE_ANON_KEY,
      hasLocalStorageUrl: Boolean(localStorage.getItem('supabase_url')),
      hasLocalStorageKey: Boolean(localStorage.getItem('supabase_key')),
    });
  }, []);
  
  const runTest = async () => {
    setIsLoading(true);
    try {
      const testResult = await testSupabaseConnection();
      setResult(testResult);
    } catch (error) {
      console.error('Error running connection test:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    // Run an initial test when the component mounts
    runTest();
  }, []);
  
  const hasError = result && (
    result.configStatus === 'missing' || 
    result.connectionStatus === 'error' || 
    result.permissionsStatus === 'error'
  );
  
  return (
    <Card className="w-full shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Supabase Connection Diagnostics
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        </CardTitle>
        <CardDescription>
          Test the connection between your application and Supabase
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {!result && !isLoading ? (
          <div className="text-center py-4">
            <p>Click the button below to run the connection test</p>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Testing connection...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col items-center p-4 border rounded-lg">
                <div className="text-lg font-medium mb-2">Configuration</div>
                {result.configStatus === 'present' ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-red-500" />
                )}
                <div className="mt-2 text-sm text-center">
                  {result.configStatus === 'present' 
                    ? 'Environment variables found' 
                    : 'Missing environment variables'}
                </div>
              </div>
              
              <div className="flex flex-col items-center p-4 border rounded-lg">
                <div className="text-lg font-medium mb-2">Connection</div>
                {result.connectionStatus === 'success' ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : result.connectionStatus === 'error' ? (
                  <AlertCircle className="h-8 w-8 text-red-500" />
                ) : (
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                )}
                <div className="mt-2 text-sm text-center">
                  {result.connectionStatus === 'success' 
                    ? 'Successfully connected' 
                    : result.connectionStatus === 'error'
                    ? 'Connection failed'
                    : 'Not tested'}
                </div>
              </div>
              
              <div className="flex flex-col items-center p-4 border rounded-lg">
                <div className="text-lg font-medium mb-2">Permissions</div>
                {result.permissionsStatus === 'success' ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : result.permissionsStatus === 'error' ? (
                  <AlertCircle className="h-8 w-8 text-red-500" />
                ) : (
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                )}
                <div className="mt-2 text-sm text-center">
                  {result.permissionsStatus === 'success' 
                    ? 'Permissions correct' 
                    : result.permissionsStatus === 'error'
                    ? 'Permission issues detected'
                    : 'Not tested'}
                </div>
              </div>
            </div>
            
            {hasError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Connection Issue Detected</AlertTitle>
                <AlertDescription>
                  <p className="mb-2">{result.error}</p>
                  {result.details && <p className="text-sm">{result.details}</p>}
                </AlertDescription>
              </Alert>
            )}
            
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Environment Variable Debug</AlertTitle>
              <AlertDescription>
                <div className="text-xs mt-1">
                  <p><strong>Env Vars Present:</strong> URL: {envDebug.hasUrlMeta ? 'Yes' : 'No'}, Key: {envDebug.hasKeyMeta ? 'Yes' : 'No'}</p>
                  <p><strong>Env Var Types:</strong> URL: {envDebug.urlType}, Key: {envDebug.keyType}</p>
                  <p><strong>LocalStorage Values:</strong> URL: {envDebug.hasLocalStorageUrl ? 'Yes' : 'No'}, Key: {envDebug.hasLocalStorageKey ? 'Yes' : 'No'}</p>
                </div>
              </AlertDescription>
            </Alert>
            
            {showFullDetails && (
              <div className="mt-4 p-4 bg-slate-100 dark:bg-slate-800 rounded-md text-xs font-mono">
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={() => setShowFullDetails(!showFullDetails)}
          disabled={!result}
        >
          {showFullDetails ? 'Hide Details' : 'Show Details'}
        </Button>
        <Button onClick={runTest} disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Run Connection Test
        </Button>
      </CardFooter>
    </Card>
  );
}
